import {
    Bell,
    CalendarClock,
    Check,
    Database,
    FileText,
    HelpCircle,
    ListChecks,
    type LucideIcon,
    Pencil,
    Phone,
    Plus,
    Search,
    Send,
    SlidersHorizontal,
    Square,
    ThumbsUp,
    UserRound,
    X,
} from 'lucide-react';

import { CONVERSATION_CONTROL_TOOL_OUTPUTS } from '@apps/web/components/phoebe_chat_view/tool_calls/conversation_control_tool_ui';
import {
    COMPACT_OUTREACH_SUGGESTION_WRITE_TOOLS,
    OUTREACH_ACTION_TOOL_NAMES,
} from '@apps/web/components/phoebe_chat_view/tool_calls/outreach_action_tool_ui';
import {
    AVAILABILITY_ACTION_TOOL_NAMES,
    MEMORY_ACTION_TOOL_NAMES,
    RECORD_ACTION_TOOL_NAMES,
} from '@apps/web/components/phoebe_chat_view/tool_calls/record_settings_tool_ui';
import { SKILL_CONTEXT_TOOL_NAMES } from '@apps/web/components/phoebe_chat_view/tool_calls/skill_context_tool_ui';
import { SETTINGS_SURFACE_TOOL_NAMES } from '@apps/web/components/phoebe_chat_view/tool_calls/surface_organization_setting_tool_ui';

export type ToolDisplayName = {
    readonly active: string;
    readonly complete: string;
};

export type ToolRendererSpec = {
    readonly icon?: LucideIcon;
    readonly displayName?: ToolDisplayName;
    readonly stepLabel?: ToolStepLabel | null;
    readonly suppressRawOutput?: boolean;
    readonly showInputs?: boolean;
};

const TOOL_ICON_REGISTRY: Record<string, LucideIcon> = {
    query: Database,
    caregivers_search: Search,
    caregivers_attribute_summary: UserRound,
    caregivers_details: UserRound,
    client_details: UserRound,
    clients_search: Search,
    get_caregiver_memory: FileText,
    get_client_details: UserRound,
    get_outreach_details: CalendarClock,
    search_caregiver_memories: Search,
    shifts_search: Search,
    shifts_details: CalendarClock,
    callouts_search: Search,
    callouts_details: CalendarClock,
    outreach_search: Search,
    search_conversation_history: Search,
    search_voice_call_transcripts: Search,
    ask_user_question: HelpCircle,
    request_user_decision: HelpCircle,
    show_dynamic_ui: HelpCircle,
    review_outreach: ListChecks,
    notify: Bell,
    override_contact_attempt_response: Pencil,
    submit_outreach_suggestion_feedback: ThumbsUp,
    add_outreach_subscriber: Plus,
    remove_outreach_subscriber: X,
    approve_staged_message: Check,
    reject_staged_message: X,
    edit_staged_message_content: Pencil,
    send_one_off_sms_in_conversation: Send,
    place_one_off_voice_call_in_conversation: Phone,
    update_playbook: FileText,
    update_callout_rules: FileText,
    monitor_outreach: CalendarClock,
    stop_agent_monitoring: Square,
    organization_scheduling_context: UserRound,
    draft_agent_outreach: CalendarClock,
    notify_caregivers: Bell,
    group_shifts_for_auto_draft: ListChecks,
    get_caregiver_group_recommendation_details: UserRound,
    get_caregiver_recommendation_details: UserRound,
    get_group_recommendations: Search,
    get_outreach_suggestion_drafts: ListChecks,
    get_recommendations: Search,
    get_shift_recommendations: Search,
    manage_outreach_add_suggestions: UserRound,
    manage_outreach_remove_suggestions: UserRound,
    manage_outreach_set_status: CalendarClock,
    manage_outreach_start_with_contacts: CalendarClock,
    manage_outreach_add_contact_attempts: CalendarClock,
    fill_callout_shifts: Check,
    assign_shifts_to_caregiver: Check,
    update_client_record: FileText,
    update_caregiver_record: FileText,
    update_client_phone_preference: FileText,
    add_caregiver_weekly_availability: CalendarClock,
    add_caregiver_one_off_availability: CalendarClock,
    update_caregiver_availability: CalendarClock,
    delete_caregiver_availability: CalendarClock,
    save_note: FileText,
    update_outreach_filter_rule: FileText,
    save_caregiver_memory_general: FileText,
    save_caregiver_memory_instruction: FileText,
    save_client_memory: FileText,
    save_client_memory_general: FileText,
    save_client_memory_instruction: FileText,
    submit_general_agent_feedback: Send,
    date_and_time: CalendarClock,
    get_current_datetime: CalendarClock,
    view_skills: ListChecks,
    load_skill: FileText,
    create_sub_agent: ListChecks,
    create_sub_agents: ListChecks,
    list_sub_agents: ListChecks,
    dump_sub_agent: FileText,
    message_sub_agent: Send,
    admin_token_alert_workflow: Bell,
    query_outreach_report: FileText,
    update_execution_plan: ListChecks,
    add_shift_note_to_ehr: FileText,
    surface_organization_setting: SlidersHorizontal,
};

const TOOL_DISPLAY_NAMES: Record<string, { active: string; complete: string }> =
    {
        'Tool execution': {
            active: 'Running tool',
            complete: 'Completed tool',
        },
        callouts_search: {
            active: 'Searching open shifts',
            complete: 'Searched open shifts',
        },
        callouts_details: {
            active: 'Looking up outreach details',
            complete: 'Looked up outreach details',
        },
        caregivers_details: {
            active: 'Looking up caregiver details',
            complete: 'Looked up caregiver details',
        },
        caregivers_attribute_summary: {
            active: 'Reviewing caregiver attributes',
            complete: 'Reviewed caregiver attributes',
        },
        client_details: {
            active: 'Looking up client details',
            complete: 'Looked up client details',
        },
        get_client_details: {
            active: 'Looking up client details',
            complete: 'Looked up client details',
        },
        get_caregiver_memory: {
            active: 'Reviewing caregiver memory',
            complete: 'Reviewed caregiver memory',
        },
        search_caregiver_memories: {
            active: 'Searching caregiver memories',
            complete: 'Searched caregiver memories',
        },
        shifts_details: {
            active: 'Looking up shift details',
            complete: 'Looked up shift details',
        },
        organization_scheduling_context: {
            active: 'Looking up organization details',
            complete: 'Looked up organization details',
        },
        date_and_time: {
            active: 'Checking current time',
            complete: 'Checked current time',
        },
        get_current_datetime: {
            active: 'Checking current time',
            complete: 'Checked current time',
        },
        query_outreach_report: {
            active: 'Building outreach report',
            complete: 'Built outreach report',
        },
        run_bash: {
            active: 'Analyzing data',
            complete: 'Analyzed data',
        },
        render_dynamic_ui: {
            active: 'Preparing data card',
            complete: 'Prepared data card',
        },
        admin_token_alert_workflow: {
            active: 'Investigating token alert',
            complete: 'Investigated token alert',
        },
        list_admin_calendars: {
            active: 'Listing admin calendars',
            complete: 'Listed admin calendars',
        },
        create_sub_agent: {
            active: 'Delegating task',
            complete: 'Delegated task',
        },
        create_sub_agents: {
            active: 'Delegating tasks',
            complete: 'Delegated tasks',
        },
        list_sub_agents: {
            active: 'Checking task progress',
            complete: 'Checked task progress',
        },
        dump_sub_agent: {
            active: 'Reading task result',
            complete: 'Read task result',
        },
        message_sub_agent: {
            active: 'Sending task follow-up',
            complete: 'Sent task follow-up',
        },
        draft_agent_outreach: {
            active: 'Creating outreach',
            complete: 'Created outreach',
        },
        notify_caregivers: {
            active: 'Notifying caregivers',
            complete: 'Notified caregivers',
        },
        get_shift_recommendations: {
            active: 'Finding caregivers',
            complete: 'Found caregiver recommendations',
        },
        manage_outreach_add_suggestions: {
            active: 'Adding caregivers',
            complete: 'Added caregivers',
        },
        manage_outreach_remove_suggestions: {
            active: 'Removing caregivers',
            complete: 'Removed caregivers',
        },
        manage_outreach_set_status: {
            active: 'Updating outreach',
            complete: 'Updated outreach',
        },
        manage_outreach_pause: {
            active: 'Pausing outreach',
            complete: 'Paused outreach',
        },
        manage_outreach_resume: {
            active: 'Resuming outreach',
            complete: 'Resumed outreach',
        },
        manage_outreach_cancel: {
            active: 'Cancelling outreach',
            complete: 'Cancelled outreach',
        },
        manage_outreach_start_with_contacts: {
            active: 'Starting outreach',
            complete: 'Started outreach',
        },
        manage_outreach_add_contact_attempts: {
            active: 'Queuing contact attempts',
            complete: 'Queued contact attempts',
        },
        fill_callout_shifts: {
            active: 'Filling outreach',
            complete: 'Filled outreach',
        },
        assign_shifts_to_caregiver: {
            active: 'Assigning shift',
            complete: 'Assigned shift',
        },
        add_shift_note_to_ehr: {
            active: 'Adding shift note',
            complete: 'Added shift note',
        },
        update_client_record: {
            active: 'Updating client',
            complete: 'Updated client',
        },
        update_caregiver_record: {
            active: 'Updating caregiver',
            complete: 'Updated caregiver',
        },
        update_client_phone_preference: {
            active: 'Updating client phone preference',
            complete: 'Updated client phone preference',
        },
        add_caregiver_weekly_availability: {
            active: 'Adding weekly availability',
            complete: 'Added weekly availability',
        },
        add_caregiver_one_off_availability: {
            active: 'Adding one-off availability',
            complete: 'Added one-off availability',
        },
        update_caregiver_availability: {
            active: 'Updating availability',
            complete: 'Updated availability',
        },
        delete_caregiver_availability: {
            active: 'Deleting availability',
            complete: 'Deleted availability',
        },
        save_note: {
            active: 'Taking a note',
            complete: 'Phoebe took a note',
        },
        save_caregiver_memory_general: {
            active: 'Saving caregiver memory',
            complete: 'Saved caregiver memory',
        },
        save_caregiver_memory_instruction: {
            active: 'Saving caregiver instruction',
            complete: 'Saved caregiver instruction',
        },
        save_client_memory: {
            active: 'Saving client memory',
            complete: 'Saved client memory',
        },
        save_client_memory_general: {
            active: 'Saving client memory',
            complete: 'Saved client memory',
        },
        save_client_memory_instruction: {
            active: 'Saving client instruction',
            complete: 'Saved client instruction',
        },
        submit_general_agent_feedback: {
            active: 'Sending feedback to Phoebe',
            complete: 'Sent feedback to Phoebe',
        },
        update_callout_rules: {
            active: 'Updating playbook',
            complete: 'Updated Phoebe\u2019s playbook',
        },
        schedule_wake_up: {
            active: 'Scheduling follow-up',
            complete: 'Scheduled follow-up',
        },
        add_contact_attempts: {
            active: 'Queuing contact attempts',
            complete: 'Queued contact attempts',
        },
        review_outreach: {
            active: 'Reviewing outreach',
            complete: 'Reviewed outreach',
        },
        notify: {
            active: 'Notifying',
            complete: 'Notified',
        },
        override_contact_attempt_response: {
            active: 'Updating caregiver response',
            complete: 'Updated caregiver response',
        },
        submit_outreach_suggestion_feedback: {
            active: 'Recording suggestion feedback',
            complete: 'Recorded suggestion feedback',
        },
        add_outreach_subscriber: {
            active: 'Adding subscriber',
            complete: 'Added subscriber',
        },
        remove_outreach_subscriber: {
            active: 'Removing subscriber',
            complete: 'Removed subscriber',
        },
        approve_staged_message: {
            active: 'Approving staged message',
            complete: 'Approved staged message',
        },
        reject_staged_message: {
            active: 'Rejecting staged message',
            complete: 'Rejected staged message',
        },
        edit_staged_message_content: {
            active: 'Editing staged message',
            complete: 'Edited staged message',
        },
        send_one_off_sms_in_conversation: {
            active: 'Sending one-off SMS',
            complete: 'Sent one-off SMS',
        },
        place_one_off_voice_call_in_conversation: {
            active: 'Placing one-off call',
            complete: 'Placed one-off call',
        },
        monitor_outreach: {
            active: 'Scheduling outreach monitoring',
            complete: 'Scheduled outreach monitoring',
        },
        stop_agent_monitoring: {
            active: 'Stopping outreach monitoring',
            complete: 'Stopped outreach monitoring',
        },
        get_outreach_suggestion_drafts: {
            active: 'Drafting caregiver suggestions',
            complete: 'Drafted caregiver suggestions',
        },
        apply_outreach_suggestion_drafts: {
            active: 'Adding outreach suggestions',
            complete: 'Added outreach suggestions',
        },
        tool_call: {
            active: 'Running tool',
            complete: 'Completed tool',
        },
        update_execution_plan: {
            active: 'Updating live status',
            complete: 'Live status',
        },
        surface_organization_setting: {
            active: 'Opening setting',
            complete: 'Setting',
        },
    };

export type ToolStepLabel = {
    readonly active: string;
    readonly complete: string;
};

const TOOL_STEP_LABEL: Record<string, ToolStepLabel | null> = {
    // Boring lookups — hidden so the plan only shows meaningful steps.
    get_current_datetime: null,
    date_and_time: null,
    organization_scheduling_context: null,
    view_skills: null,
    load_skill: null,
    // The plan-declaration tool itself never appears as a step — its
    // arguments ARE the steps. Filter it out so the fallback grouping
    // path doesn't render a redundant row.
    update_execution_plan: null,
    // User questions render as dedicated answer cards, not execution-plan
    // work.
    ask_user_question: null,
    request_user_decision: null,
    show_dynamic_ui: null,

    run_bash: {
        active: 'Analyzing data',
        complete: 'Analyzed data',
    },
    render_dynamic_ui: {
        active: 'Preparing data card',
        complete: 'Prepared data card',
    },

    // Admin subagent orchestration.
    create_sub_agent: {
        active: 'Delegating task',
        complete: 'Delegated task',
    },
    create_sub_agents: {
        active: 'Delegating tasks',
        complete: 'Delegated tasks',
    },
    list_sub_agents: {
        active: 'Checking task progress',
        complete: 'Checked task progress',
    },
    dump_sub_agent: {
        active: 'Reading task result',
        complete: 'Read task result',
    },
    message_sub_agent: {
        active: 'Sending task follow-up',
        complete: 'Sent task follow-up',
    },

    // Schedule / shift discovery.
    schedule_summary: {
        active: 'Reviewing schedule',
        complete: 'Reviewed schedule',
    },
    callouts_search: {
        active: 'Finding open shifts',
        complete: 'Found open shifts',
    },
    shifts_search: { active: 'Finding shifts', complete: 'Found shifts' },
    shifts_details: {
        active: 'Reviewing shift details',
        complete: 'Reviewed shift details',
    },

    // Caregivers.
    caregivers_search: {
        active: 'Finding caregivers',
        complete: 'Found caregivers',
    },
    caregivers_details: {
        active: 'Reviewing caregiver',
        complete: 'Reviewed caregiver',
    },
    caregivers_attribute_summary: {
        active: 'Reviewing caregivers',
        complete: 'Reviewed caregivers',
    },
    get_shift_recommendations: {
        active: 'Finding caregivers',
        complete: 'Found caregivers',
    },
    get_recommendations: {
        active: 'Finding caregivers',
        complete: 'Found caregivers',
    },
    get_group_recommendations: {
        active: 'Finding caregivers',
        complete: 'Found caregivers',
    },
    get_caregiver_recommendation_details: {
        active: 'Reviewing caregiver recommendation',
        complete: 'Reviewed caregiver recommendation',
    },
    get_caregiver_group_recommendation_details: {
        active: 'Reviewing caregiver recommendations',
        complete: 'Reviewed caregiver recommendations',
    },
    group_shifts_for_auto_draft: {
        active: 'Grouping shifts',
        complete: 'Grouped shifts',
    },

    // Clients.
    clients_search: { active: 'Finding clients', complete: 'Found clients' },
    client_details: { active: 'Reviewing client', complete: 'Reviewed client' },
    get_client_details: {
        active: 'Reviewing client',
        complete: 'Reviewed client',
    },

    // Callouts / outreach.
    callouts_details: {
        active: 'Reviewing outreach',
        complete: 'Reviewed outreach',
    },
    get_outreach_details: {
        active: 'Reviewing outreach',
        complete: 'Reviewed outreach',
    },
    outreach_search: {
        active: 'Reviewing outreach',
        complete: 'Reviewed outreach',
    },
    manage_outreach_start_with_contacts: {
        active: 'Starting outreach',
        complete: 'Started outreach',
    },
    manage_outreach_add_contact_attempts: {
        active: 'Sending outreach',
        complete: 'Sent outreach',
    },
    manage_outreach_pause: {
        active: 'Pausing outreach',
        complete: 'Paused outreach',
    },
    manage_outreach_resume: {
        active: 'Resuming outreach',
        complete: 'Resumed outreach',
    },
    manage_outreach_cancel: {
        active: 'Cancelling outreach',
        complete: 'Cancelled outreach',
    },
    manage_outreach_add_suggestions: {
        active: 'Adding caregivers',
        complete: 'Added caregivers',
    },
    manage_outreach_remove_suggestions: {
        active: 'Removing caregivers',
        complete: 'Removed caregivers',
    },
    manage_outreach_set_status: {
        active: 'Updating outreach',
        complete: 'Updated outreach',
    },
    monitor_outreach: {
        active: 'Scheduling outreach monitoring',
        complete: 'Scheduled outreach monitoring',
    },
    notify_caregivers: {
        active: 'Notifying caregivers',
        complete: 'Notified caregivers',
    },
    draft_agent_outreach: {
        active: 'Creating outreach',
        complete: 'Created outreach',
    },
    fill_callout_shifts: {
        active: 'Filling shifts',
        complete: 'Filled shifts',
    },
    assign_shifts_to_caregiver: {
        active: 'Assigning shift',
        complete: 'Assigned shift',
    },
    add_shift_note_to_ehr: {
        active: 'Adding shift note',
        complete: 'Added shift note',
    },

    // Writes / mutations.
    update_client_record: {
        active: 'Updating client',
        complete: 'Updated client',
    },
    update_client_phone_preference: {
        active: 'Updating client phone',
        complete: 'Updated client phone',
    },
    update_caregiver_record: {
        active: 'Updating caregiver',
        complete: 'Updated caregiver',
    },
    add_caregiver_weekly_availability: {
        active: 'Adding availability',
        complete: 'Added availability',
    },
    add_caregiver_one_off_availability: {
        active: 'Adding availability',
        complete: 'Added availability',
    },
    update_caregiver_availability: {
        active: 'Updating availability',
        complete: 'Updated availability',
    },
    delete_caregiver_availability: {
        active: 'Deleting availability',
        complete: 'Deleted availability',
    },
    // Deliberately hidden from the plan projection: the "Phoebe took a
    // note" chip in the transcript is the note's only surface, and a plan
    // step would narrate the same save a second time.
    save_note: null,
    save_caregiver_memory_general: {
        active: 'Saving caregiver memory',
        complete: 'Saved caregiver memory',
    },
    save_caregiver_memory_instruction: {
        active: 'Saving caregiver instruction',
        complete: 'Saved caregiver instruction',
    },
    save_client_memory: {
        active: 'Saving client memory',
        complete: 'Saved client memory',
    },
    save_client_memory_general: {
        active: 'Saving client memory',
        complete: 'Saved client memory',
    },
    save_client_memory_instruction: {
        active: 'Saving client instruction',
        complete: 'Saved client instruction',
    },
    submit_general_agent_feedback: {
        active: 'Sending feedback to Phoebe',
        complete: 'Sent feedback to Phoebe',
    },
    update_outreach_filter_rule: {
        active: 'Updating outreach rules',
        complete: 'Updated outreach rules',
    },
    update_callout_settings: {
        active: 'Updating callout settings',
        complete: 'Updated callout settings',
    },
    update_organization_general_settings: {
        active: 'Updating org settings',
        complete: 'Updated org settings',
    },
    update_recommendation_defaults: {
        active: 'Updating recommendation defaults',
        complete: 'Updated recommendation defaults',
    },
};

export const DOMAIN_RENDERED_TOOL_OUTPUTS = new Set<string>([
    ...OUTREACH_ACTION_TOOL_NAMES,
    ...AVAILABILITY_ACTION_TOOL_NAMES,
    'callouts_details',
    'callouts_search',
    'caregivers_attribute_summary',
    'caregivers_details',
    'caregivers_search',
    'client_details',
    'clients_search',
    ...CONVERSATION_CONTROL_TOOL_OUTPUTS,
    'date_and_time',
    'get_current_datetime',
    'save_note',
    'get_caregiver_group_recommendation_details',
    'get_caregiver_memory',
    'get_caregiver_recommendation_details',
    'get_client_details',
    'get_group_recommendations',
    'get_outreach_details',
    'get_outreach_suggestion_drafts',
    'get_recommendations',
    'get_shift_recommendations',
    'group_shifts_for_auto_draft',
    ...MEMORY_ACTION_TOOL_NAMES,
    'organization_scheduling_context',
    'outreach_search',
    'query_outreach_report',
    ...RECORD_ACTION_TOOL_NAMES,
    'search_caregiver_memories',
    'search_conversation_history',
    'search_voice_call_transcripts',
    'shifts_details',
    'shifts_search',
    ...SKILL_CONTEXT_TOOL_NAMES,
    ...SETTINGS_SURFACE_TOOL_NAMES,
    'update_execution_plan',
    'update_outreach_filter_rule',
]);

const INPUT_HIDDEN_TOOL_OUTPUTS = new Set<string>([
    'get_outreach_suggestion_drafts',
    ...COMPACT_OUTREACH_SUGGESTION_WRITE_TOOLS,
    ...DOMAIN_RENDERED_TOOL_OUTPUTS,
]);

export const TOOL_RENDERER_SPECS: Record<string, ToolRendererSpec> =
    Object.fromEntries(
        [
            ...new Set([
                ...Object.keys(TOOL_ICON_REGISTRY),
                ...Object.keys(TOOL_DISPLAY_NAMES),
                ...Object.keys(TOOL_STEP_LABEL),
                ...DOMAIN_RENDERED_TOOL_OUTPUTS,
                ...INPUT_HIDDEN_TOOL_OUTPUTS,
            ]),
        ].map((toolName) => {
            const icon = TOOL_ICON_REGISTRY[toolName];
            const displayName = TOOL_DISPLAY_NAMES[toolName];
            const hasConfiguredStepLabel = Object.prototype.hasOwnProperty.call(
                TOOL_STEP_LABEL,
                toolName,
            );
            const spec = {
                ...(icon ? { icon } : {}),
                ...(displayName ? { displayName } : {}),
                ...(hasConfiguredStepLabel
                    ? { stepLabel: TOOL_STEP_LABEL[toolName] ?? null }
                    : {}),
                ...(DOMAIN_RENDERED_TOOL_OUTPUTS.has(toolName)
                    ? { suppressRawOutput: true }
                    : {}),
                ...(INPUT_HIDDEN_TOOL_OUTPUTS.has(toolName)
                    ? { showInputs: false }
                    : {}),
            } satisfies ToolRendererSpec;
            return [toolName, spec];
        }),
    );

export function getToolIcon(toolName: string | null): LucideIcon | undefined {
    return toolName ? TOOL_RENDERER_SPECS[toolName]?.icon : undefined;
}

export function getConfiguredToolDisplayName(
    toolName: string,
): ToolDisplayName | undefined {
    return TOOL_RENDERER_SPECS[toolName]?.displayName;
}

export function getConfiguredToolStepLabel(
    toolName: string | null,
): ToolStepLabel | null | undefined {
    if (toolName === null) {
        return null;
    }
    return TOOL_RENDERER_SPECS[toolName]?.stepLabel;
}

export function isDomainRenderedToolOutput(toolName: string): boolean {
    return TOOL_RENDERER_SPECS[toolName]?.suppressRawOutput === true;
}

export function shouldShowToolInputs(toolName: string): boolean {
    return TOOL_RENDERER_SPECS[toolName]?.showInputs ?? true;
}

export function isSilentInternalToolCall(
    toolName: string | null,
    args: Record<string, unknown> | Array<unknown> | null,
): boolean {
    // An agent-detected feature_gap is a silent internal capture: rendering
    // the tool step would leak the gap (and its inputs) to the coordinator
    // the agent is explicitly told not to mention it to. Coordinator-
    // requested feedback (other categories) stays visible. Hiding is
    // category-aware because both uses share one tool name.
    return (
        toolName === 'submit_general_agent_feedback' &&
        args !== null &&
        !Array.isArray(args) &&
        args['category'] === 'feature_gap'
    );
}
